import React from "react";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import { DateTime } from "luxon";
import useQuery from "./useQuery";
import { nullable } from "diagnostics-lib/io";
import { uniq } from "diagnostics-lib/array";
function* fillPeriodGap(min: DateTime, max: DateTime): Generator<DateTime> {
  let cursor = max.endOf("month");
  while (cursor > min) {
    yield cursor;
    cursor = cursor.minus({ months: 1 });
  }
}

function fillReportingDates(start: DateTime, end: DateTime) {
  return Array.from(fillPeriodGap(start, end))
    .filter(
      date =>
        date.startOf("month") > start.startOf("month") &&
        date.startOf("month") < end.startOf("month"),
    )
    .reverse();
}

function fillData<
  T extends { period: number; values: { projectId: string }[]; driftedProjectIds: string[] }[],
>(initialPeriods: T) {
  const periodWithDriftedProjects = initialPeriods.reduce(
    (acc, period, i) => {
      const previousPeriod = acc[i - 1];
      if (!previousPeriod) return acc;
      const currentPeriodProjectIdToFill = previousPeriod.values.filter(
        ({ projectId }) =>
          !period.values.some(({ projectId: currentProjectId }) => currentProjectId === projectId),
      );

      const nextPeriodValues = [...period.values, ...currentPeriodProjectIdToFill];

      const nextPeriod = {
        period: period.period,
        driftedProjectIds: uniq(currentPeriodProjectIdToFill.map(({ projectId }) => projectId)),
        values: nextPeriodValues,
      };

      return [...acc, nextPeriod];
    },
    initialPeriods[0] ? [initialPeriods[0]] : [],
  ) as T;

  const result = periodWithDriftedProjects.reduce(
    (acc, period, i, arr) => {
      const previousPeriod = arr[i - 1];
      if (!previousPeriod) return acc;

      const previousPeriodDate = previousPeriod.period;
      const periodDate = period.period;
      const filledPeriodDates = fillReportingDates(
        DateTime.fromMillis(previousPeriodDate),
        DateTime.fromMillis(periodDate),
      );
      const driftedProjectIds = previousPeriod.values.map(({ projectId }) => projectId);
      const filledPeriods = filledPeriodDates.map(date => ({
        period: date.startOf("month").toMillis(),
        driftedProjectIds: Array.from(new Set(driftedProjectIds)),
        values: previousPeriod.values,
      }));
      return [...acc, ...filledPeriods, period];
    },
    initialPeriods[0] ? [initialPeriods[0]] : [],
  ) as T;

  return result;
}

export const DEFAULT_PROJECTS_FILTER = {
  ids: [],
  siteIds: [],
  typeIds: [],
  onlyProd: true,
};

export const useProjectsFilter = createGlobalState({
  defaultValue: DEFAULT_PROJECTS_FILTER,
  key: "fleet-view-projects-filter",
  scoped: false,
  storage: "session",
  type: io.strict({
    ids: io.array(io.string),
    siteIds: io.array(nullable(io.string)),
    typeIds: io.array(nullable(io.string)),
    onlyProd: io.boolean,
  }),
});

export const useProjectsDisplayDraft = () => {
  const [projectFilters, setProjectFilters] = useProjectsFilter({});
  const displayDraft = !projectFilters.onlyProd;
  const setDisplayDraft = React.useCallback(
    (value: boolean) => setProjectFilters({ ...projectFilters, onlyProd: !value }),
    [projectFilters, setProjectFilters],
  );

  return [displayDraft, setDisplayDraft] as const;
};

export const useFailuresFilter = createGlobalState({
  defaultValue: {
    degradationTypeIds: [],
    equipmentTypeIds: [],
    failureCategoryIds: [],
    systemIds: [],
  },
  key: "fleet-view-failures-filter",
  scoped: false,
  storage: "session",
  type: io.strict({
    systemIds: io.array(nullable(io.string)),
    equipmentTypeIds: io.array(nullable(io.string)),
    degradationTypeIds: io.array(nullable(io.string)),
    failureCategoryIds: io.array(nullable(io.string)),
  }),
});

export const useFilledData = createGlobalState({
  storage: "session",
  key: "fleet-view-fill-incomplete-data",
  type: io.boolean,
  defaultValue: true,
  scoped: false,
});

export function useDiagnosisReporting() {
  const [projectsFilter] = useProjectsFilter({});
  const [failuresFilter] = useFailuresFilter({});
  const [filledData] = useFilledData({});

  const rawData = useQuery("/reporting/diagnosis", {
    body: {
      projectsFilter,
      failuresFilter,
    },
  }).body;

  // TODO do it server side
  return React.useMemo(() => {
    const sortedPeriods = rawData.periods
      .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime())
      .map(period => ({
        ...period,
        driftedProjectIds: [],
      }));
    const periods = filledData ? fillData(sortedPeriods) : sortedPeriods;
    const range = sortedPeriods.length
      ? ({
          min: new Date(sortedPeriods[0]!.period).toISOString(),
          max: new Date(sortedPeriods[sortedPeriods.length - 1]!.period).toISOString(),
        } as const)
      : null;

    return {
      ...rawData,
      periods,
      range,
    };
  }, [rawData, filledData]);
}

export type DiagnosisReporting = ReturnType<typeof useDiagnosisReporting>;

export function usePerformanceReporting() {
  const [projectsFilter] = useProjectsFilter({});
  const [filledData] = useFilledData({});

  const rawData = useQuery("/reporting/performance", {
    body: {
      projectsFilter,
    },
  }).body;

  // TODO do it server side
  return React.useMemo(() => {
    const sortedPeriods = rawData.periods
      .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime())
      .map(period => ({
        ...period,
        driftedProjectIds: [],
      }));

    const periods = filledData ? fillData(sortedPeriods) : sortedPeriods;
    const range = sortedPeriods.length
      ? ({
          min: new Date(sortedPeriods[0]!.period).toISOString(),
          max: new Date(sortedPeriods[sortedPeriods.length - 1]!.period).toISOString(),
        } as const)
      : null;

    return {
      ...rawData,
      periods,
      range,
    };
  }, [rawData, filledData]);
}

export type PerformanceReporting = ReturnType<typeof usePerformanceReporting>;

export type Reporting = DiagnosisReporting | PerformanceReporting;
export type ReportingPeriod = {
  readonly min: string;
  readonly max: string;
};
